import {
    AbortedVegnettAction,
    ActionType,
    ErrorAction,
    FetchVegnettAction,
    GetState,
    ReceivedVegnettAction,
} from '@/actions/actiontypes';
import { Data } from '@/bootstrap/data';
import { Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import { stateMap, stateOmrader, stateVegnett, stateVegnettFilter } from '@/selectors/selectors';
import {
    MapState,
    OmraderState,
    RootState,
    Severity,
    VegkartError,
    VegnettState,
    VegnettStatistics,
} from '@/state';
import { Dispatch } from '@/state/store';
import { getFylkerForQuery } from './fetchHelpers';
import { Integration } from './integration';
import { CommonQueries, MapQueries, OmraderQueries, VegnettQueries } from './queries';

const LIMIT = 5000;

const geometryTolerance = (featureCount: number) => {
    if (featureCount > 3000) return 30;
    else if (featureCount > 2000) return 20;
    else if (featureCount > 1000) return 10;
    else return null;
};

export const fetchVegnettV4 = () => async (dispatch, getState: () => RootState, integration: Integration) => {
    // States
    const mapState: MapState = stateMap(getState());
    const omraderState: OmraderState = stateOmrader(getState());
    const vegnettState: VegnettState = stateVegnett(getState());
    const staticData: Data = integration.data;

    // Timestamp
    const timestamp = getState().searchState.searchDropdownState.timestamp;

    // Query classes
    const commonQueries: CommonQueries = new CommonQueries();
    const mapQueries: MapQueries = mapState.toQueries();
    const omraderQueries: OmraderQueries = omraderState.toQueries();
    const vegnettQueries: VegnettQueries = stateVegnettFilter(getState());

    // If filter contains empty arrays, don't fetch and reset road net results.
    if (vegnettQueries.hasEmptyArrays()) {
        const resetVegnettAction: ReceivedVegnettAction = {
            type: ActionType.VEGNETT_FETCH_RECEIVED,
            receivedVegnettState: vegnettState.withRoadNetLinks([]).withRoadNetStatistics([]),
        };
        dispatch(resetVegnettAction);
        return;
    }

    // Dispatch loading state.
    const fetchVegnettAction: FetchVegnettAction = { type: ActionType.VEGNETT_FETCH_INITIATED };
    dispatch(fetchVegnettAction);

    if (mapState.zoom < 6) {
        const roadnetStatistics = (
            await integration.server.getVegnettStatisticsGruppert(mapState.kartutsnitt.toQueryString(), [
                'fylke',
            ])
        )
            .map(res => {
                const fylke = getFylkerForQuery(omraderQueries, staticData.fylker)?.find(
                    f => f?.nummer === res?.fylke
                );
                return fylke ? new VegnettStatistics(fylke, res.antall) : null;
            })
            .filter(res => res != null);

        const receivedVegnettState = vegnettState
            .withRoadNetLinks(null)
            .withRoadNetStatistics(roadnetStatistics)
            .withFilter(vegnettQueries)
            .withLoading(false);
        let action: ReceivedVegnettAction | AbortedVegnettAction = {
            type: ActionType.VEGNETT_FETCH_RECEIVED,
            receivedVegnettState,
        };

        if (hasSearchStateChanged(mapQueries, omraderQueries, vegnettQueries, getState)) {
            action = {
                type: ActionType.VEGNETT_FETCH_ABORTED,
            };
        }
        dispatch(action);
        return;
    }

    if (mapState.zoom > 13) {
        updateVegnettWithPaginationLoop(
            commonQueries,
            mapQueries,
            omraderQueries,
            vegnettQueries,
            integration,
            getState,
            omraderQueries,
            timestamp,
            dispatch
        );
        return;
    }

    // Begin fetch.
    findRoadSysRefAndTotalsFromProbeResults(mapQueries, omraderQueries, integration)
        .then(response => {
            const antall = response.total;
            const commonQueries = new CommonQueries().withGeometritoleranse(geometryTolerance(antall));
            if (antall > LIMIT) {
                const roadNetStatistics = Array.from(response.fylke?.entries()).map(
                    entry =>
                        new VegnettStatistics(
                            staticData.fylker.find(f => f.nummer === entry[0]),
                            entry[1]
                        )
                );

                const receivedVegnettState = vegnettState
                    .withRoadNetLinks(null)
                    .withRoadNetStatistics(roadNetStatistics)
                    .withFilter(vegnettQueries)
                    .withLoading(false);
                let action: ReceivedVegnettAction | AbortedVegnettAction = {
                    type: ActionType.VEGNETT_FETCH_RECEIVED,
                    receivedVegnettState,
                };

                if (hasSearchStateChanged(mapQueries, omraderQueries, vegnettQueries, getState)) {
                    action = {
                        type: ActionType.VEGNETT_FETCH_ABORTED,
                    };
                }

                dispatch(action);
            } else {
                updateVegnettWithPaginationLoop(
                    commonQueries,
                    mapQueries,
                    omraderQueries,
                    vegnettQueries,
                    integration,
                    getState,
                    omraderQueries.withVegsystemreferanser(
                        response.roadRef ?? omraderQueries.vegsystemreferanse
                    ),
                    timestamp,
                    dispatch
                );
            }
        })
        .catch(rejection => {
            const receivedVegnettAction: ReceivedVegnettAction = {
                type: ActionType.VEGNETT_FETCH_RECEIVED,
                receivedVegnettState: stateVegnett(getState()).withLoading(false),
            };
            dispatch(receivedVegnettAction);

            const error = new VegkartError(
                'Feil ved henting av vegnett.',
                { response: rejection.response },
                Severity.ERROR
            );
            const errorAction: ErrorAction = { type: ActionType.ERROR_OCCURRED, error };
            dispatch(errorAction);
        });
};

const updateVegnettWithPaginationLoop = async (
    genericQueries: CommonQueries,
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    integration: Integration,
    getState: GetState,
    adjustedOmraderQueries: OmraderQueries,
    timestamp: Date,
    dispatch: Dispatch
): Promise<void> => {
    const allLinks: Vegnettlenke[] = [];
    let start = genericQueries.start;

    while (true) {
        if (hasSearchStateChanged(mapQueries, omraderQueries, vegnettQueries, getState)) {
            dispatch({ type: ActionType.VEGNETT_FETCH_ABORTED });
            return;
        }

        const query = genericQueries.withStart(start);
        const fragment = await integration.server.getSegmentertVegnett(
            query,
            mapQueries,
            adjustedOmraderQueries,
            vegnettQueries,
            timestamp
        );

        allLinks.push(...fragment.objekter);

        if (fragment.metadata.returnert < fragment.metadata.sidestorrelse) {
            break;
        }

        start = fragment.metadata.neste.start;
    }

    dispatch({
        type: ActionType.VEGNETT_FETCH_RECEIVED,
        receivedVegnettState: stateVegnett(getState())
            .withRoadNetLinks(allLinks)
            .withRoadNetStatistics(null)
            .withFilter(vegnettQueries)
            .withLoading(false),
    });
};

interface ProbeResult {
    roadRef: string[] | null;
    fylke: Map<number, number> | null;
    total: number;
}

async function findRoadSysRefAndTotalsFromProbeResults(
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    integration: Integration
): Promise<ProbeResult> {
    const refResult = new Map<string, number>();
    const countyResult = new Map<number, number>();

    const res = await integration.server.getVegnettStatisticsGruppert(mapQueries.kartutsnitt, [
        'vegkategori',
        'fylke',
    ]);

    res.forEach(group => {
        if (omraderQueries.fylke.length != 0 && !omraderQueries.fylke.includes(group.fylke.toString()))
            return;

        if (group.vegkategori)
            refResult.set(group.vegkategori, (refResult.get(group.vegkategori) ?? 0) + group.antall);

        if (group.fylke) countyResult.set(group.fylke, (countyResult.get(group.fylke) ?? 0) + group.antall);
    });

    const refTotal = getTotal(refResult);
    if (refTotal < LIMIT) return { roadRef: null, fylke: countyResult, total: refTotal };

    const roadRefRequestAll = ['E', 'R', 'F', 'K', 'P', 'S'];
    const roadRefRequest = [...roadRefRequestAll];

    while (getTotal(refResult) > LIMIT && refResult.size > 0) {
        refResult.delete(roadRefRequest.pop() ?? '');
    }

    return refResult.size > 0
        ? { roadRef: roadRefRequest, fylke: countyResult, total: getTotal(refResult) }
        : { roadRef: roadRefRequestAll, fylke: countyResult, total: refTotal };
}

function getTotal(map: Map<string, number>): number {
    let total = 0;

    for (const value of map.values()) {
        total += value;
    }

    return total;
}

const hasSearchStateChanged = (
    mapQueries: MapQueries,
    omraderQueries: OmraderQueries,
    vegnettQueries: VegnettQueries,
    getState: GetState
): boolean => {
    return (
        JSON.stringify(stateMap(getState()).toQueries()) !== JSON.stringify(mapQueries) ||
        JSON.stringify(stateOmrader(getState()).toQueries()) !== JSON.stringify(omraderQueries) ||
        JSON.stringify(stateVegnett(getState()).toQueries()) !== JSON.stringify(vegnettQueries)
    );
};
